import { Divider, Grid, ScrollArea, Table } from '@mantine/core';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { NoDataFoundTableRow } from '../../../components/DataTable';
import { Detail } from '../../../components/Detail';
import { InfoBadge } from '../../../components/InfoBadge';
import { LoadingRows } from '../../../components/LoadingRows';
import { useFilter } from '../../../hooks/useFilter';
import { queryWithFilter } from '../../../util/api/queryWithFilter';
import {
	formatSecondsToHours,
	formatToDateString,
	formatToTimeString,
} from '../../../util/formatting';
import type { AttendanceDto } from '../../attendance/types/AttendanceDto';
import type { FullOperatorDto } from '../types/OperatorDto';
import { KeyedRow } from '../../../components/KeyedRow';

export function StaffGeneralInfo({
	operator,
	isLoading,
}: { operator?: FullOperatorDto; isLoading: boolean }) {
	return (
		<Grid w={'100%'}>
			<Grid.Col span={6}>
				<Detail
					isLoading={isLoading}
					label={'Username'}
					stringValue={operator?.username}
				/>
			</Grid.Col>
			<Grid.Col span={6}>
				<Detail
					isLoading={isLoading}
					label={'Card Number'}
					isUndefined={!operator?.cardNo}
				>
					<InfoBadge value={operator?.cardNo} />
				</Detail>
			</Grid.Col>
			<Grid.Col span={6}>
				<Detail
					isLoading={isLoading}
					label={'Branch'}
					stringValue={operator?.branch}
				/>
			</Grid.Col>
			<Grid.Col span={6}>
				<Detail
					isLoading={isLoading}
					label={'Job Title'}
					stringValue={operator?.jobTitle}
				/>
			</Grid.Col>
			<Divider w={'100%'} />
			<Grid.Col span={6}>
				<Detail
					isLoading={isLoading}
					label={'First Name'}
					stringValue={operator?.firstName}
				/>
			</Grid.Col>
			<Grid.Col span={6}>
				<Detail
					isLoading={isLoading}
					label={'Last Name'}
					stringValue={operator?.surname}
				/>
			</Grid.Col>
			<Grid.Col span={6}>
				<Detail
					isLoading={isLoading}
					label={'Phone'}
					isUndefined={!operator?.phone}
				>
					<InfoBadge value={operator?.phone} />
				</Detail>
			</Grid.Col>
			<Grid.Col span={6}>
				<Detail
					isLoading={isLoading}
					label={'Email'}
					isUndefined={!operator?.email}
				>
					<InfoBadge value={operator?.email} />
				</Detail>
			</Grid.Col>
		</Grid>
	);
}

export function StaffAddress({
	operator,
	isLoading,
}: { operator?: FullOperatorDto; isLoading: boolean }) {
	return (
		<Grid w={'100%'}>
			<Grid.Col span={6}>
				<Detail
					isLoading={isLoading}
					label={'Address Line One'}
					stringValue={operator?.addressLineOne}
				/>
			</Grid.Col>
			<Grid.Col span={6}>
				<Detail
					isLoading={isLoading}
					label={'Address Line Two'}
					stringValue={operator?.addressLineTwo}
				/>
			</Grid.Col>
			<Grid.Col span={4}>
				<Detail
					isLoading={isLoading}
					label={'Post Code'}
					stringValue={operator?.postCode}
				/>
			</Grid.Col>
			<Grid.Col span={4}>
				<Detail
					isLoading={isLoading}
					label={'County'}
					stringValue={operator?.county}
				/>
			</Grid.Col>
			<Grid.Col span={4}>
				<Detail
					isLoading={isLoading}
					label={'Town'}
					stringValue={operator?.town}
				/>
			</Grid.Col>
		</Grid>
	);
}

export function StaffEmergencyContact({
	operator,
	isLoading,
}: { operator?: FullOperatorDto; isLoading: boolean }) {
	return (
		<Grid w={'100%'}>
			<Grid.Col span={6}>
				<Detail
					isLoading={isLoading}
					label={'Contact Name'}
					stringValue={operator?.emergencyContactName}
				/>
			</Grid.Col>
			<Grid.Col span={6}>
				<Detail
					isLoading={isLoading}
					label={'Number'}
					stringValue={operator?.emergencyContactNumber}
				/>
			</Grid.Col>
		</Grid>
	);
}

export function StaffAttendance() {
	const { id, organisation } = useParams();
	const filter = useFilter();

	const attendanceQuery = queryWithFilter<AttendanceDto[]>(
		`${organisation}/attendance/operator/${id}`,
		filter.value,
	);

	const isDataFound = useMemo(() => {
		return (
			attendanceQuery.data?.value && attendanceQuery.data?.value.length > 0
		);
	}, [attendanceQuery.data?.value]);

	return (
		<ScrollArea h={'90%'}>
			<Table stickyHeader>
				<Table.Thead>
					<KeyedRow>
						<Table.Th>Till Name</Table.Th>
						<Table.Th>Start Date</Table.Th>
						<Table.Th>Start Time</Table.Th>
						<Table.Th>End Date</Table.Th>
						<Table.Th>End Time</Table.Th>
						<Table.Th>Total Time</Table.Th>
						<Table.Th />
					</KeyedRow>
				</Table.Thead>
				<Table.Tbody>
					{!attendanceQuery.isLoading ? (
						<>
							{!isDataFound && <NoDataFoundTableRow />}
							{attendanceQuery.data?.value.map((attendance) => (
								<KeyedRow>
									<Table.Td>{attendance.tillName}</Table.Td>
									<Table.Td>
										{formatToDateString(attendance.startTime)}
									</Table.Td>
									<Table.Td>
										{formatToTimeString(attendance.startTime)}
									</Table.Td>
									<Table.Td>{formatToDateString(attendance.endTime)}</Table.Td>
									<Table.Td>{formatToTimeString(attendance.endTime)}</Table.Td>
									<Table.Td>
										{formatSecondsToHours(attendance.totalSeconds)}
									</Table.Td>
								</KeyedRow>
							))}
						</>
					) : (
						<LoadingRows rows={3} />
					)}
				</Table.Tbody>
			</Table>
		</ScrollArea>
	);
}
